<template>
    <div class="space-y-6">
        <div class="space-y-2 bg-white ring-1 ring-black/10 rounded-lg shadow-sm overflow-hidden">
            <div class="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div class="flex-1 flex items-center space-x-4">
                    <h1 class="text-lg font-medium">Active Subscription</h1>
                    <BadgeSubscriptionExpiry v-if="currentSubscription" :subscription="currentSubscription" />
                </div>
                <div class="flex-shrink-0 flex items-center space-x-2">
                    <template v-if="currentSubscription">
                        <UButton
                            @click="emit('cancel')"
                            variant="ghost"
                            color="red" 
                            label="Cancel" 
                            icon="i-ph-x-circle-bold"
                        />
                        <UButton
                            @click="emit('extend')"
                            color="emerald" 
                            label="Extend" 
                            icon="i-ph-clock-clockwise-bold"
                        />
                    </template>
                    <UButton
                        v-else
                        @click="emit('create')"
                        color="blue"
                        label="Create Subscription" 
                        icon="i-ph-plus-bold"
                    />
                </div>
            </div>
            <div v-if="!currentSubscription" class="flex flex-col space-y-1 items-center justify-center p-8">
                <UIcon name="i-ph-shield-slash-duotone" class="size-5 text-gray-400" />
                <span class="text-gray-400 text-[13px] italic">No active subscription.</span>
            </div>
            <div v-else class="grid grid-cols-4 gap-5 px-6 py-5">
                <div class="col-span-1">
                    <p class="text-xs uppercase leading-6 font-medium text-gray-500">ID</p>
                    <p v-if="currentSubscription?.id" class="font-mono">{{ currentSubscription.id.split('-').at(-1)?.toUpperCase() }}</p>
                    <p v-else class="text-gray-400">--</p>
                </div>
                <div class="col-span-1">
                    <p class="text-xs uppercase leading-6 font-medium text-gray-500">Partner</p>
                    <div v-if="currentSubscription.partner" class="flex items-center space-x-2.5">
                        <Avatar bucket="media" folder="partners" size="sm" :placeholder="currentSubscription.partner.name" :path="currentSubscription.partner.avatar" />
                        <span>{{ currentSubscription.partner.name }}</span>
                    </div>
                    <p v-else class="text-gray-400">--</p>
                </div>
                <div class="col-span-1">
                    <p class="text-xs uppercase leading-6 font-medium text-gray-500">Source</p>
                    <UTooltip v-if="currentSubscription.source_type && currentSubscription.source_values?.length" class="select-none" :ui="{ base: '!h-auto' }">
                        <BadgeSubscriptionSourceType v-if="currentSubscription.source_type" :type="currentSubscription.source_type" />
                        <template #text>
                            <span v-if="currentSubscription.source_values.length === 1">{{ currentSubscription.source_values[0] }}</span>
                            <div v-else class="p-2 space-y-1">
                                <Field 
                                    v-for="value in currentSubscription.source_values" 
                                    :label="getField(value).key" 
                                    label-class="text-[11px] uppercase text-gray-400"
                                    inline
                                >
                                    <span :class="getField(value).key === 'exclusion' ? 'block max-w-[16rem] whitespace-normal break-words' : ''">{{ getField(value).value }}</span>
                                </Field>
                            </div>
                        </template>
                    </UTooltip>
                    <p v-else class="text-gray-400">--</p>
                </div>
                <div v-if="currentExclusion" class="col-span-1">
                    <p class="text-xs uppercase leading-6 font-medium text-gray-500">Exclusion</p>
                    <TruncatedText :value="currentExclusion" />
                </div>
                <div class="col-span-1">
                    <p class="text-xs uppercase leading-6 font-medium text-gray-500">Invoice</p>
                    <ULink v-if="currentSubscription.invoice_id" :to="`/invoices/partners/${currentSubscription.invoice_id}`" class="font-mono hover:underline">
                        {{ currentSubscription.invoice_id.split('-').at(-1)?.toUpperCase() }}
                    </ULink>
                    <UBadge v-else color="gray" variant="solid" label="Uninvoiced" />
                </div>
                <div class="col-span-1">
                    <p class="text-xs uppercase leading-6 font-medium text-gray-500">Start Date</p>
                    <p>{{ dayjs(currentSubscription.created_at).format('MMM D, YYYY') }}</p>
                </div>
                <div class="col-span-1">
                    <p class="text-xs uppercase leading-6 font-medium text-gray-500">Expiry Date</p>
                    <p>{{ dayjs(currentSubscription.expires_at).format('MMM D, YYYY') }}</p>
                </div>
            </div>
        </div>

        <div class="space-y-2 bg-white ring-1 ring-black/10 rounded-lg shadow-sm overflow-hidden">
            <div class="bg-gray-50 border-b border-gray-200 px-6 py-4">
                <h1 class="text-lg font-medium">Subscriptions History</h1>
            </div>
            <div v-if="!subscriptionsHistory?.length" class="flex flex-col space-y-1 items-center justify-center p-8">
                <UIcon name="i-ph-clock-counter-clockwise-duotone" class="size-5 text-gray-400" />
                <span class="text-gray-400 text-[13px] italic">No previous subscriptions.</span>
            </div>
            <UTable
                v-else
                :rows="subscriptionsHistory"
                :columns="tableColumns"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm' }"
                class="h-full"
            >
                <template #id-data="{ row }">
                    <p class="font-mono">
                        {{ row.id.split('-').at(-1)?.toUpperCase() }}
                    </p>
                </template>
                <template #partner-data="{ row }">
                    <div v-if="row.partner" class="flex items-center space-x-2.5">
                        <Avatar bucket="media" folder="partners" size="sm" :placeholder="row.partner.name" :path="row.partner.avatar" />
                        <span>{{ row.partner.name }}</span>
                    </div>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #source-data="{ row }">
                    <UTooltip v-if="row.source_type && row.source_values?.length" class="select-none" :ui="{ base: '!h-auto' }">
                        <BadgeSubscriptionSourceType v-if="row.source_type" :type="row.source_type" />
                        <template #text>
                            <span v-if="row.source_values.length === 1">{{ row.source_values[0] }}</span>
                            <div v-else class="p-2 space-y-1">
                                <Field 
                                    v-for="value in row.source_values" 
                                    :label="getField(value).key" 
                                    label-class="text-[11px] uppercase text-gray-400"
                                    inline
                                >
                                    <span :class="getField(value).key === 'exclusion' ? 'block max-w-[16rem] whitespace-normal break-words' : ''">{{ getField(value).value }}</span>
                                </Field>
                            </div>
                        </template>
                    </UTooltip>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #invoice-data="{ row }">
                    <ULink v-if="row.invoice_id" :to="`/invoices/partners/${row.invoice_id}`" class="font-mono hover:underline">
                        {{ row.invoice_id.split('-').at(-1)?.toUpperCase() }}
                    </ULink>
                    <UBadge v-else color="gray" variant="solid" label="Uninvoiced" />
                </template>
                <template #starts-data="{ row }">
                    <p>{{ dayjs(row.created_at).format('MMM D, YYYY') }}</p>
                </template>
                <template #expired-data="{ row }">
                    <p>{{ dayjs(row.expires_at).format('MMM D, YYYY') }}</p>
                </template>
            </UTable>
        </div>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import type { Subscription } from '~/types/subscription';

const emit = defineEmits<{
    'create': [void],
    'extend': [void],
    'cancel': [void],
}>()

const props = defineProps<{
    currentSubscription?: Subscription
    subscriptionsHistory?: Subscription[]
}>()

// exclusion lives in the source credential fields (source_values: "key | value"),
// not on the customer record. Surface it on the active subscription card.
const currentExclusion = computed(() => {
    const match = props.currentSubscription?.source_values?.find(v => v.startsWith('exclusion | '))
    return match ? getField(match).value : ''
})

const tableColumns = [
    { key: 'id', label: 'ID' },
    { key: 'partner', label: 'Partner' },
    { key: 'source', label: 'Source' },
    { key: 'invoice', label: 'Invoice' },
    { key: 'starts', label: 'Starts On' },
    { key: 'expired', label: 'Expired On' },
]

function getField(field: string) {
    const [ key, value ] = field.split(' | ')
    return { key: key.replaceAll('_', ' '), value }
}
</script>